import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import EditIcon from '@mui/icons-material/Edit'
import HighlightOffIcon from '@mui/icons-material/HighlightOff'
import { Button, Chip, Stack } from '@mui/material'
import { DateTime } from 'luxon'
import PropTypes from 'prop-types'
import { Link as RouterLink } from 'react-router-dom'
import MeterConnectionCell from '@/features/dashboard/admin/MeterConnectionCell'
import useAuth from '@/hooks/useAuth'
import { describeLastReading } from '@/lib/freshnessCopy'
import { meterCategoryLabel } from '@/lib/meterCategories'
import { ADMIN_ROLES } from '@/lib/roles'
import { hasRole } from '@/utils/hasRole'
import EntityLink from '../EntityLink'
import { METER_TYPES, meterName, subMeters } from '../relations'
import MeterRows from './MeterRows'
import PeekLayout, {
    PeekDetails,
    PeekPlaceholder,
    PeekSection
} from './PeekLayout'
import UsageSummary from './UsageSummary'
import { hasUsage } from './useDailyUsage'
import useRelatedRecords from './useRelatedRecords'

// Per-fuel differences: which list holds the meter, what identifies it, and
// where it's edited (water meters have no edit page).
const FUEL = {
    'electricity-meter': {
        list: 'electricity',
        identifiers: (m) => [
            m.meterNumber && `Meter number ${m.meterNumber}`,
            m.group && `Group ${m.group}`
        ],
        firstReading: (m) => m.firstReading,
        editPath: (m) => `/admin/power/meters/${m.id}/edit`
    },
    'gas-meter': {
        list: 'gas',
        identifiers: (m) => [
            m.mprn && `MPRN ${m.mprn}`,
            m.meter_serial_number && `Serial ${m.meter_serial_number}`
        ],
        firstReading: (m) => m.first_reading,
        editPath: (m) => `/admin/gas/meters/${m.id}/edit`
    },
    'water-meter': {
        list: 'water',
        identifiers: (m) => [
            m.spid && `SPID ${m.spid}`,
            m.meter_serial_number && `Serial ${m.meter_serial_number}`
        ],
        firstReading: (m) => m.first_reading,
        editPath: null
    }
}

const yesNo = (value) =>
    value ? (
        <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            component="span"
        >
            <CheckCircleOutlineIcon color="success" fontSize="small" />
            <span>Yes</span>
        </Stack>
    ) : (
        <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            component="span"
        >
            <HighlightOffIcon color="error" fontSize="small" />
            <span>No</span>
        </Stack>
    )

/** A meter: where it is, what it's connected to and what it has recorded. */
export default function MeterPeek({ type, id }) {
    const records = useRelatedRecords()
    const { user } = useAuth()
    const fuel = FUEL[type]
    const { label, fuel: fuelName } = METER_TYPES[type]
    const meters = records[fuel.list]
    const meter = meters.find(
        (candidate) => String(candidate.id) === String(id)
    )

    if (!meter) {
        return <PeekPlaceholder kind={label} loading={records.isLoading} />
    }

    const firstReading = fuel.firstReading(meter)
    const children = subMeters(meters, meter.id)

    return (
        <PeekLayout
            kind={label}
            title={meterName(type, meter)}
            subtitle={fuel.identifiers(meter).filter(Boolean).join(' · ')}
            actions={
                // SLT can view meters but only admins can edit them.
                fuel.editPath && hasRole(user, ADMIN_ROLES) ? (
                    <Button
                        component={RouterLink}
                        to={fuel.editPath(meter)}
                        size="small"
                        variant="outlined"
                        startIcon={<EditIcon fontSize="small" />}
                    >
                        Edit meter
                    </Button>
                ) : null
            }
        >
            {hasUsage(fuelName) ? (
                <UsageSummary
                    fuel={fuelName}
                    meterIds={[meter.id]}
                    label="Use"
                />
            ) : null}

            <PeekSection title="Details">
                <PeekDetails
                    rows={[
                        {
                            label: 'Location',
                            value: meter.location ? (
                                <EntityLink
                                    type="location"
                                    id={meter.location.id}
                                >
                                    {meter.location.isamsName}
                                </EntityLink>
                            ) : (
                                <Chip
                                    label="Not set"
                                    color="error"
                                    size="small"
                                />
                            )
                        },
                        // openspec meter-categories: the admin-set category, as a chip,
                        // only when set (water meters are never categorised).
                        meter.category && {
                            label: 'Category',
                            value: (
                                <Chip
                                    label={meterCategoryLabel(meter.category)}
                                    size="small"
                                    variant="outlined"
                                />
                            )
                        },
                        {
                            label: 'Parent meter',
                            value: meter.parent ? (
                                <EntityLink type={type} id={meter.parent.id}>
                                    {meterName(type, meter.parent)}
                                </EntityLink>
                            ) : (
                                '—'
                            )
                        },
                        type !== 'water-meter' && {
                            label: 'Connection',
                            value: (
                                <MeterConnectionCell
                                    owningConfig={meter.owning_config}
                                />
                            )
                        },
                        {
                            label: 'First reading',
                            value: firstReading
                                ? DateTime.fromISO(firstReading).toLocaleString(
                                      DateTime.DATE_MED
                                  )
                                : 'None yet'
                        },
                        type === 'electricity-meter' && {
                            label: 'Latest data',
                            value: describeLastReading(meter.lastReadingAt).text
                        },
                        type === 'gas-meter' && {
                            label: 'Daily metered',
                            value: yesNo(meter.daily_metered)
                        },
                        type !== 'water-meter' && {
                            label: 'On the map',
                            value: yesNo(meter.location_point)
                        }
                    ]}
                />
            </PeekSection>

            {children.length > 0 ? (
                <PeekSection title={`Sub-meters (${children.length})`}>
                    <MeterRows
                        entries={children.map((child) => ({
                            type,
                            meter: child
                        }))}
                    />
                </PeekSection>
            ) : null}
        </PeekLayout>
    )
}

MeterPeek.propTypes = {
    id: PropTypes.string.isRequired,
    type: PropTypes.oneOf(Object.keys(METER_TYPES)).isRequired
}
